import type {
  CPSCard,
  CPECard,
  VitaleCard,
  InsertedCard,
} from '@state/reducers/appReducer'

export interface CVCardData {
  firstName: string
  lastName: string
  dateOfBirth: string
}

// Re-export types for convenience
export type CPSCardInfo = CPSCard
export type CPECardInfo = CPECard
export type VitaleCardInfo = VitaleCard
export type CardInfo = InsertedCard

export enum CardReaderStates {
  CARD_STATE_UPDATE = 'card_service:card-state-update',
}

export interface WebSocketMessage<T = unknown> {
  name: string
  data: T
}

const CARD_STATE_UPDATE = 'card_service:card-state-update'

class CardStateService {
  private socket: WebSocket | null = null
  private listeners: Set<(cards: CardInfo[]) => void> = new Set()

  private url = ''
  private shouldReconnect = true
  private reconnectAttempts = 0
  private maxAttempts = 5
  private reconnectDelay = 2000
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null

  get isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN
  }

  connect(url: string): void {
    this.url = url
    this.shouldReconnect = true
    this.createConnection()
  }

  private createConnection(): void {
    this.socket = new WebSocket(this.url)

    this.socket.onopen = () => {
      console.log('Connected to card reader')
      this.reconnectAttempts = 0
    }

    this.socket.onclose = () => {
      console.log('Disconnected from card reader')
      this.handleReconnect()
    }

    this.socket.onerror = (error) => {
      console.error('⚠️ WebSocket error:', error)
    }

    this.socket.onmessage = (event) => {
      try {
        const message: WebSocketMessage<CardInfo[]> = JSON.parse(event.data)

        if (message.name === CARD_STATE_UPDATE) {
          this.listeners.forEach((callback) => callback(message.data))
        }
      } catch (e) {
        console.log('Non-JSON message, ignoring', e)
      }
    }
  }

  private handleReconnect(): void {
    if (!this.shouldReconnect) return

    if (this.reconnectAttempts >= this.maxAttempts) {
      return
    }
    this.reconnectAttempts++

    this.reconnectTimer = setTimeout(() => {
      this.createConnection()
    }, this.reconnectDelay)
  }

  onCardUpdate(callback: (cards: CardInfo[]) => void): () => void {
    this.listeners.add(callback)
    return () => this.listeners.delete(callback)
  }

  // Send method (for testing with echo server)
  send(message: WebSocketMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      const data = JSON.stringify(message)
      console.log('Sending:', data)
      this.socket.send(data)
    } else {
      console.log('Cannot send, socket not open')
    }
  }

  disconnect(): void {
    this.shouldReconnect = false

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    this.socket?.close()
    this.socket = null
  }
}

export default CardStateService
